<!--
  TimePicker — pick one time: type it, nudge it, or choose a day.

  Typing ("nov 1", "11/1", "fri", "+2w", "jan 8 5pm"), a quick pick or a click
  on a day only PROPOSES a date: the preview shows it in full beside what it
  was, and nothing is set until Set, Enter or a double-click on a day. A missed
  click on a small target never saves. ↓ / ↑ move the proposal a day later /
  earlier (Shift: a week), the way the calendar below reads.

  Emits `set` with the stored columns (start_at, end_at, all_day, timezone),
  `clear`, and `cancel`.
-->
<template>
  <div class="time-picker">
    <div class="entry">
      <font-awesome-icon :icon="kindIcon" class="entry-icon" fixed-width />
      <input
        ref="inputRef"
        v-model="text"
        :placeholder="placeholder"
        :aria-label="label"
        @keydown="onKeydown"
      />
    </div>

    <div class="preview">
      <template v-if="proposal">
        <strong>{{ longDay(proposal.date, proposal.time) }}</strong>
        <span class="context">{{ context }}</span>
      </template>
      <span v-else-if="error" class="is-error">{{ error }}</span>
      <span v-else>Type, pick or choose a day.</span>
    </div>

    <div class="picks">
      <button
        v-for="pick in picks"
        :key="pick.label"
        type="button"
        class="btn btn-sm"
        :class="{ 'is-selected': proposal?.date === pick.date }"
        @click="propose(pick.date)"
      >
        {{ pick.label }}
      </button>
    </div>

    <div class="calendar">
      <div class="month">
        <button
          type="button"
          class="nav"
          aria-label="Previous month"
          @click="view = addMonths(view, -1)"
        >
          <font-awesome-icon icon="chevron-left" />
        </button>
        <span>{{ monthLabel }}</span>
        <button
          type="button"
          class="nav"
          aria-label="Next month"
          @click="view = addMonths(view, 1)"
        >
          <font-awesome-icon icon="chevron-right" />
        </button>
      </div>
      <div class="days">
        <span v-for="(d, i) in ['S', 'M', 'T', 'W', 'T', 'F', 'S']" :key="i" class="weekday">{{
          d
        }}</span>
        <button
          v-for="day in days"
          :key="day.date"
          type="button"
          class="day"
          :class="{
            'is-outside': !day.inMonth,
            'is-today': day.date === today,
            'is-current': day.date === current?.date,
            'is-selected': day.date === proposal?.date,
          }"
          :aria-label="longDay(day.date)"
          @click="propose(day.date)"
          @dblclick="propose(day.date) || set()"
        >
          {{ day.n }}
        </button>
      </div>
    </div>

    <p class="keys">
      <kbd>↓</kbd> later <kbd>↑</kbd> earlier <kbd>⇧</kbd> a week <kbd>↵</kbd> set
      <kbd>esc</kbd> cancel
    </p>

    <div class="actions">
      <button v-if="current" type="button" class="clear" @click="emit('clear')">Clear</button>
      <span v-else />
      <div class="buttons">
        <button type="button" class="btn btn-sm" @click="emit('cancel')">Cancel</button>
        <button type="button" class="btn btn-sm btn-primary" :disabled="!changed" @click="set">
          {{ setLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { getBrowserTimeZone } from "@auger/ui/utils/date"
import {
  addDays,
  addMonths,
  isoDay,
  isParsed,
  longDay,
  parseWhen,
  relativeDay,
  shortDay,
  storedInstants,
  TIME_KINDS,
  type TimeKind,
  timeParts,
  type TimeRow,
  todayDay,
  weekdayOf,
} from "@auger/ui/utils/times"

const props = withDefaults(
  defineProps<{
    /** The field's row, or null when it's empty. */
    value: TimeRow | null
    /** The kind of time this field holds. */
    kind?: TimeKind
    /** Typed text to start with (a key pressed on the cell). */
    seed?: string
    label?: string
  }>(),
  { kind: "instant", seed: "", label: "Date" }
)

const emit = defineEmits<{
  set: [columns: { start_at: string; end_at: string | null; all_day: boolean; timezone: string }]
  clear: []
  cancel: []
}>()

const today = todayDay()
/** The field's kind, as its selected cell shows it. */
const kindIcon = computed(() => TIME_KINDS.find((k) => k.key === props.kind)?.icon ?? "calendar")
const current = computed(() => (props.value ? timeParts(props.value) : null))
const tz = computed(() => current.value?.tz ?? getBrowserTimeZone())

const text = ref(
  props.seed || (current.value ? shortDay(current.value.date, current.value.time) : "")
)
const view = ref(`${(current.value?.date ?? today).slice(0, 7)}-01`)
const inputRef = ref<HTMLInputElement | null>(null)

onMounted(() =>
  nextTick(() => {
    const input = inputRef.value
    if (!input) return
    input.focus()
    // A seeded key is the start of what's typed; an existing value is replaced.
    if (props.seed) input.setSelectionRange(input.value.length, input.value.length)
    else input.select()
  })
)

const parsed = computed(() =>
  parseWhen(text.value, { base: current.value?.date ?? null, ranges: props.kind !== "instant" })
)
const proposal = computed(() => (isParsed(parsed.value) ? parsed.value : null))
const error = computed(() => ("error" in parsed.value ? parsed.value.error : null))

// The calendar follows what's typed.
watch(proposal, (p) => {
  if (p) view.value = `${p.date.slice(0, 7)}-01`
})

const changed = computed(() => {
  const p = proposal.value
  if (!p) return false
  const c = current.value
  return !c || p.date !== c.date || (p.time ?? null) !== (c.allDay ? null : c.time)
})

const context = computed(() => {
  const p = proposal.value!
  const c = current.value
  if (!c) return relativeDay(p.date, today)
  return changed.value
    ? `${relativeDay(p.date, today)} · was ${shortDay(c.date, c.allDay ? null : c.time)}`
    : `${relativeDay(p.date, today)} · current`
})

const setLabel = computed(() =>
  changed.value
    ? `Set ${shortDay(proposal.value!.date, proposal.value!.time)}`
    : current.value
      ? "No change"
      : "Set"
)
const placeholder = computed(() =>
  props.kind === "instant" ? "Nov 1, 11/1, fri, +2w…" : "Nov 1, fri 2-3pm…"
)

/** Propose a day, keeping a typed time. Returns false so `@dblclick` can chain. */
function propose(date: string): false {
  text.value = shortDay(date, proposal.value?.time ?? null)
  return false
}

function set() {
  const p = proposal.value
  if (!p || !changed.value) return
  emit("set", {
    ...storedInstants(
      {
        kind: props.kind,
        date: p.date,
        time: p.time ?? null,
        endDate: props.kind === "instant" ? null : (p.endDate ?? null),
        endTime: p.endTime ?? null,
        allDay: p.allDay,
      },
      tz.value
    ),
    timezone: tz.value,
  })
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Enter") {
    event.preventDefault()
    set()
  } else if (event.key === "Escape") {
    event.preventDefault()
    emit("cancel")
  } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault()
    const from = proposal.value?.date ?? current.value?.date ?? today
    const step = (event.key === "ArrowDown" ? 1 : -1) * (event.shiftKey ? 7 : 1)
    propose(addDays(from, step))
  }
}

const picks = computed(() => [
  { label: "Today", date: today },
  { label: "Tomorrow", date: addDays(today, 1) },
  { label: "+1 wk", date: addDays(today, 7) },
  { label: "+1 mo", date: addMonths(today, 1) },
])

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]
const monthLabel = computed(() => {
  const [y = 0, m = 1] = view.value.split("-").map(Number)
  return `${MONTHS[m - 1]} ${y}`
})
const days = computed(() => {
  const [y = 0, m = 1] = view.value.split("-").map(Number)
  const first = isoDay(y, m, 1)
  const start = addDays(first, -weekdayOf(first))
  return Array.from({ length: 42 }, (_, i) => {
    const date = addDays(start, i)
    return { date, n: Number(date.slice(8)), inMonth: Number(date.slice(5, 7)) === m }
  })
})
</script>

<style>
.time-picker {
  display: flex;
  flex-direction: column;
  width: 280px;
  font-variant-numeric: tabular-nums;

  /* The proposed day sits in from the edges the buttons below fill. */
  .preview {
    padding-left: 6px;
    padding-right: 6px;
  }

  .entry {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;

    input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      font: inherit;
      color: inherit;
    }
  }

  .entry-icon {
    color: var(--auger-text-weak);
  }

  /* The proposed day, with how far away it is pulled to the right. */
  .preview {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 2px 8px;
    min-height: 28px;
    padding-top: 6px;
    padding-bottom: 4px;
    color: var(--auger-text-weak);

    strong {
      color: var(--auger-text-strong);
    }

    .context {
      margin-left: auto;
      text-align: right;
    }

    .is-error {
      color: var(--auger-danger);
    }
  }

  .picks {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    padding-bottom: 6px;

    .btn {
      margin: 0;
    }

    .btn {
      padding: 0 4px;
    }

    .btn.is-selected {
      background: var(--auger-bg);
      border-color: var(--auger-primary);
      color: var(--auger-primary-dark);
    }
  }

  .calendar {
    padding-top: 2px;
    border-top: 1px solid var(--auger-border-light);
  }

  .month {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 28px;
    font-weight: 600;
  }

  .nav {
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 4px;
    background: none;
    color: var(--auger-text-weak);
    cursor: pointer;

    &:hover {
      background: var(--auger-bg-muted);
    }
  }

  .days {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 2px;
  }

  .weekday {
    text-align: center;
    font-size: 0.9em;
    color: var(--auger-text-light);
  }

  .day {
    height: 28px;
    border: none;
    border-radius: 5px;
    background: none;
    font: inherit;
    color: inherit;
    cursor: pointer;

    &:hover {
      background: var(--auger-bg-muted);
    }

    &.is-outside {
      color: var(--auger-text-light);
    }

    &.is-today {
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    &.is-current {
      box-shadow: inset 0 0 0 1.5px var(--auger-border-hover);
    }

    &.is-selected {
      background: var(--auger-primary);
      color: var(--auger-text-inverted);
      box-shadow: none;
      font-weight: 600;
    }
  }

  .keys {
    margin: 6px 0 0;
    font-size: 0.9em;
    color: var(--auger-text-light);

    kbd {
      padding: 0 3px;
      border: 1px solid var(--auger-border);
      border-radius: 3px;
      font-family: var(--auger-font-mono);
      font-size: 0.9em;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--auger-border-light);

    .buttons {
      margin: 0;
      gap: 6px;
    }

    .btn {
      margin: 0;
    }
  }

  .clear {
    border: none;
    background: none;
    font: inherit;
    color: var(--auger-danger);
    cursor: pointer;
  }
}
</style>
